/* eslint-disable react/no-unescaped-entities */
import { MapPin, Crown } from 'lucide-react';
import { PageHero } from '../components/SectionHeading';
import { ScrollReveal } from '../components/Motion';
import { ASSETS, COUNTRIES, pick } from '../data/content';
import { useLang } from '../i18n/LanguageContext';

const ACTIVE = COUNTRIES.filter((c) => c.kind === 'active' || c.kind === 'hq');
const HISTORICAL = COUNTRIES.filter((c) => c.kind === 'historical');

const FOOTPRINT_EN = [
    ['Saudi Arabia (Jeddah)', 'Middle East', 'Headquarters & Group Management', '—', '—'],
    ['Afghanistan', 'Central Asia', 'Integrated Gas Development (SECA)', 'Upstream + Midstream + Downstream', 'USD 16–21 Bn (30 years)'],
    ['Afghanistan', 'Central Asia', 'CENTGAZ Pipeline', 'Midstream', 'Within SECA'],
    ['Venezuela', 'Latin America', 'Barrancas Area Gas Project', 'Upstream + Midstream + Downstream', 'USD 1,860 M (48 months)'],
    ['Uganda', 'Sub-Saharan Africa', 'Uganda 5BB Project', 'Upstream Exploration', 'USD 40.6 M (2-well program)'],
    ['Algeria', 'North Africa', 'Illizi South PSA', 'Exploration & Development', 'USD 5.4 Bn'],
];
const FOOTPRINT_AR = [
    ['المملكة العربية السعودية (جدة)', 'الشرق الأوسط', 'المقر الرئيسي وإدارة المجموعة', '—', '—'],
    ['أفغانستان', 'آسيا الوسطى', 'تطوير الغاز المتكامل (SECA)', 'علوي + أوسط + سفلي', '16–21 مليار دولار (30 سنة)'],
    ['أفغانستان', 'آسيا الوسطى', 'أنبوب سنتغاز', 'القطاع الأوسط', 'ضمن SECA'],
    ['فنزويلا', 'أمريكا اللاتينية', 'مشروع غاز منطقة بارانكاس', 'علوي + أوسط + سفلي', '1,860 مليون دولار (48 شهرًا)'],
    ['أوغندا', 'إفريقيا جنوب الصحراء', 'مشروع أوغندا 5BB', 'استكشاف القطاع العلوي', '40.6 مليون دولار (برنامج بئرين)'],
    ['الجزائر', 'شمال إفريقيا', 'اتفاقية تقاسم إنتاج إليزي الجنوبي', 'استكشاف وتطوير', '5.4 مليار دولار'],
];

const HISTORICAL_TABLE_EN = [
    ['Azerbaijan / Caspian Sea', '1991–1998', 'AIOC — ACG Fields ("Contract of the Century")'],
    ['Azerbaijan', '1994 onwards', 'BTC Pipeline (Delta Hess)'],
    ['Egypt — Nile Delta', '1999–2007', 'Centurion Energy — El Wastani (40,000 BOEPD)'],
    ['Tunisia', '1999–2007', 'Centurion Energy — Exploration'],
    ['Nigeria / São Tomé', 'JDZ Early 2000s', 'Centurion Energy — Block 4 Exploration'],
    ['Algeria', '2023 onwards', 'Sonatrach engagement — upstream & midstream'],
];
const HISTORICAL_TABLE_AR = [
    ['أذربيجان / بحر قزوين', '1991–1998', 'AIOC — حقول ACG ("عقد القرن")'],
    ['أذربيجان', '1994 فصاعدًا', 'أنبوب BTC (دلتا-هيس)'],
    ['مصر — دلتا النيل', '1999–2007', 'سنتوريون للطاقة — الوسطاني (40,000 برميل مكافئ نفطي/يوم)'],
    ['تونس', '1999–2007', 'سنتوريون للطاقة — الاستكشاف'],
    ['نيجيريا / ساو تومي', 'JDZ أوائل 2000', 'سنتوريون للطاقة — استكشاف القطعة 4'],
    ['الجزائر', '2023 فصاعدًا', 'التعاون مع سوناطراك — القطاعان العلوي والأوسط'],
];

const HQ_BODY_EN = "Delta International Group Ltd. is headquartered in Jeddah — Saudi Arabia's commercial capital and Red Sea gateway city. From Jeddah, the company's leadership directs its global investment strategy, manages its sovereign and institutional relationships, and oversees the full portfolio of energy development programs across Central Asia, Latin America, and Sub-Saharan Africa.";
const HQ_BODY_AR = 'يقع المقر الرئيسي لمجموعة دلتا الدولية المحدودة في جدة — العاصمة التجارية للمملكة العربية السعودية وبوابتها على البحر الأحمر. ومن جدة، تدير قيادة الشركة استراتيجيتها الاستثمارية العالمية، وتدير علاقاتها السيادية والمؤسسية، وتشرف على كامل محفظة برامج تطوير الطاقة عبر آسيا الوسطى وأمريكا اللاتينية وأفريقيا جنوب الصحراء.';
const HQ_QUOTE_EN = '"Being a Saudi company is not merely an administrative fact for Delta. It is a strategic asset."';
const HQ_QUOTE_AR = '"إن كون دلتا شركة سعودية ليس مجرد حقيقة إدارية. بل هو أصل استراتيجي."';

const GlobalPresence = () => {
    const { t, lang } = useLang();
    const FOOTPRINT = lang === 'ar' ? FOOTPRINT_AR : FOOTPRINT_EN;
    const HISTORICAL_TABLE = lang === 'ar' ? HISTORICAL_TABLE_AR : HISTORICAL_TABLE_EN;
    return (
        <div data-testid="global-page">
            <PageHero
                eyebrow={t('global.hero_eyebrow')}
                title={t('global.hero_title')}
                sub={t('global.hero_sub')}
                image={ASSETS.jeddah}
            />

            {/* HQ */}
            <section className="bg-cream py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <ScrollReveal className="lg:col-span-5">
                        <div className="bg-navy-deep text-white p-10 relative grain-overlay">
                            <Crown className="w-8 h-8 text-gold mb-6" />
                            <div className="eyebrow mb-3">{t('global.hq_label')}</div>
                            <h2 className="editorial-h2 text-white text-2xl md:text-3xl mb-6">
                                {t('global.hq_h2')}
                            </h2>
                            <div className="hairline-gold w-16 my-6" />
                            <div className="space-y-3 text-white/80 text-sm">
                                <div>2827 Hassan Naib Al Haram Street</div>
                                <div>8989 Al Andalus District</div>
                                <div>Jeddah 23326, Kingdom of Saudi Arabia</div>
                                <div>+966 12 422 4097</div>
                                <div>info@saudideltaenergy.com / info@deltagroup-intl.com</div>
                            </div>
                        </div>
                    </ScrollReveal>
                    <ScrollReveal delay={0.1} className="lg:col-span-7">
                        <p className="text-ink/80 text-base md:text-lg leading-relaxed">
                            {lang === 'ar' ? HQ_BODY_AR : HQ_BODY_EN}
                        </p>
                        <blockquote className="mt-10 border-l-2 border-gold pl-6 font-display italic text-navy text-lg md:text-xl leading-snug">
                            {lang === 'ar' ? HQ_QUOTE_AR : HQ_QUOTE_EN}
                        </blockquote>
                    </ScrollReveal>
                </div>
            </section>

            {/* World Map */}
            <section className="bg-navy-deep py-24 md:py-32 relative grain-overlay">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <ScrollReveal>
                        <div className="max-w-2xl mb-12">
                            <div className="eyebrow mb-5">{t('global.map_eyebrow')}</div>
                            <h2 className="editorial-h2 text-white text-3xl md:text-4xl">
                                {t('global.map_h2')}
                            </h2>
                        </div>
                    </ScrollReveal>

                    {/* Stylized world map SVG */}
                    <div data-testid="world-map" className="relative w-full bg-navy/40 border border-gold/15 p-6 overflow-hidden">
                        <svg viewBox="0 0 100 65" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
                            {/* Background grid */}
                            <defs>
                                <pattern id="grid" width="2" height="2" patternUnits="userSpaceOnUse">
                                    <path d="M 2 0 L 0 0 0 2" fill="none" stroke="rgba(201,169,97,0.06)" strokeWidth="0.05" />
                                </pattern>
                            </defs>
                            <rect width="100" height="65" fill="url(#grid)" />

                            {/* Continent silhouettes (rough outlines) */}
                            <g fill="rgba(255,255,255,0.06)" stroke="rgba(201,169,97,0.25)" strokeWidth="0.15">
                                {/* North America */}
                                <path d="M 6 18 Q 14 14 18 16 Q 24 18 26 24 Q 28 30 24 34 Q 18 36 12 32 Q 6 26 6 18 Z" />
                                {/* South America */}
                                <path d="M 24 38 Q 30 36 32 42 Q 34 50 30 58 Q 26 62 22 56 Q 20 48 24 38 Z" />
                                {/* Europe */}
                                <path d="M 46 22 Q 52 20 56 22 Q 58 26 56 30 Q 52 32 48 30 Q 44 28 46 22 Z" />
                                {/* Africa */}
                                <path d="M 48 34 Q 56 32 60 38 Q 62 48 58 56 Q 52 60 48 54 Q 46 44 48 34 Z" />
                                {/* Asia */}
                                <path d="M 60 18 Q 72 16 82 22 Q 88 28 84 34 Q 78 38 70 36 Q 62 32 60 26 Q 58 22 60 18 Z" />
                                {/* Australia */}
                                <path d="M 80 50 Q 88 48 90 52 Q 90 58 86 58 Q 80 58 80 50 Z" />
                            </g>

                            {/* Country pins */}
                            {COUNTRIES.map((c) => {
                                const isActive = c.kind === 'active' || c.kind === 'hq';
                                const color = isActive ? '#C9A961' : 'rgba(255,255,255,0.4)';
                                const size = c.kind === 'hq' ? 1.6 : 1.2;
                                return (
                                    <g key={c.code}>
                                        {isActive && (
                                            <circle cx={c.cx} cy={c.cy} r={size + 2} fill="#C9A961" opacity="0.15">
                                                <animate attributeName="r" values={`${size};${size + 3};${size}`} dur="2.5s" repeatCount="indefinite" />
                                                <animate attributeName="opacity" values="0.4;0;0.4" dur="2.5s" repeatCount="indefinite" />
                                            </circle>
                                        )}
                                        <circle cx={c.cx} cy={c.cy} r={size} fill={color} stroke="#0B1E3F" strokeWidth="0.2" />
                                        <text x={c.cx + 2} y={c.cy - 0.5} fill={color} fontSize="1.6" fontFamily="Manrope" fontWeight="600" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            {pick(c, 'name', lang)}
                                        </text>
                                    </g>
                                );
                            })}
                        </svg>

                        <div className="flex items-center justify-center gap-8 mt-8 text-xs">
                            <div className="flex items-center gap-2 text-white/80">
                                <span className="w-3 h-3 rounded-full bg-gold" />
                                <span className="tracking-widest uppercase">{t('common.active')}</span>
                            </div>
                            <div className="flex items-center gap-2 text-white/60">
                                <span className="w-3 h-3 rounded-full bg-white/40" />
                                <span className="tracking-widest uppercase">{t('common.historical')}</span>
                            </div>
                        </div>
                    </div>

                    {/* Country call-out cards */}
                    <div className="mt-16">
                        <div className="eyebrow text-gold mb-6">{t('global.active_label')}</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gold/15">
                            {ACTIVE.map((c) => (
                                <ScrollReveal key={c.code} className="bg-navy-deep p-8 hover:bg-navy-mid transition-colors group">
                                    <div className="flex items-center justify-between mb-5">
                                        <div className="flex items-center gap-3">
                                            {c.kind === 'hq' ? <Crown className="w-5 h-5 text-gold" /> : <MapPin className="w-5 h-5 text-gold" />}
                                            <span className="text-[10px] tracking-[0.2em] uppercase text-white/55">{pick(c, 'region', lang)}</span>
                                        </div>
                                        <span className="font-display text-gold/40 text-xl">{c.code}</span>
                                    </div>
                                    <h3 className="font-display text-white text-2xl mb-3 group-hover:text-gold transition-colors">{pick(c, 'name', lang)}</h3>
                                    <p className="text-gold text-[11px] tracking-widest uppercase mb-3">{pick(c, 'program', lang)}</p>
                                    <p className="text-white/65 text-sm leading-relaxed">{pick(c, 'body', lang)}</p>
                                </ScrollReveal>
                            ))}
                        </div>
                    </div>

                    {/* Historical */}
                    <div className="mt-16">
                        <div className="eyebrow text-white/60 mb-6">{t('global.historical_label')}</div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
                            {HISTORICAL.map((c) => (
                                <div key={c.code} className="bg-navy-deep p-6">
                                    <div className="text-[10px] tracking-widest uppercase text-white/45 mb-2">{pick(c, 'region', lang)}</div>
                                    <h4 className="font-display text-white/85 text-lg">{pick(c, 'name', lang)}</h4>
                                    <p className="text-white/55 text-xs mt-2">{pick(c, 'program', lang)}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Footprint table */}
            <section className="bg-cream py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-5">{t('global.footprint_eyebrow')}</div>
                    <h2 className="editorial-h2 text-navy text-3xl md:text-4xl mb-12">{t('global.footprint_h2')}</h2>
                    <div className="overflow-x-auto">
                        <table className="editorial-table w-full" data-testid="footprint-table">
                            <thead>
                                <tr>
                                    <th>Geography</th>
                                    <th>Region</th>
                                    <th>Program</th>
                                    <th>Stage</th>
                                    <th>Investment Scale</th>
                                </tr>
                            </thead>
                            <tbody>
                                {FOOTPRINT.map((row, i) => (
                                    <tr key={i}>
                                        {row.map((cell, j) => (
                                            <td key={j} className={j === 0 ? 'font-display text-navy whitespace-nowrap' : 'text-ink/75'}>{cell}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* Historical Geographies table */}
            <section className="bg-white py-20">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-5">{t('global.historical_geo')}</div>
                    <div className="overflow-x-auto">
                        <table className="editorial-table w-full">
                            <thead>
                                <tr>
                                    <th>Geography</th>
                                    <th>Period</th>
                                    <th>Program</th>
                                </tr>
                            </thead>
                            <tbody>
                                {HISTORICAL_TABLE.map((row, i) => (
                                    <tr key={i}>
                                        {row.map((cell, j) => (
                                            <td key={j} className={j === 0 ? 'font-display text-navy' : 'text-ink/75'}>{cell}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default GlobalPresence;
